import { useState, useLayoutEffect, useMemo } from "react";
import { useTheme } from "next-themes";
import type { OnboardModalTheme } from "@coti-io/coti-wallet-plugin";

export type OnboardThemeMode = "light" | "dark";

const THEME_STORAGE_KEY = "coti-portal-theme";

/** Portal index.css tokens — avoids getComputedStyle when html class lags next-themes. */
const ONBOARD_PALETTES: Record<
  OnboardThemeMode,
  {
    foreground: string;
    mutedForeground: string;
    primary: string;
    primaryForeground: string;
    border: string;
    modalBg: string;
    modalInset: string;
    overlayBlur: string;
  }
> = {
  light: {
    foreground: "hsl(222 47% 11%)",
    mutedForeground: "hsl(215 16% 47%)",
    primary: "hsl(234 91% 54%)",
    primaryForeground: "hsl(0 0% 100%)",
    border: "hsl(220 13% 91%)",
    modalBg: "hsl(0 0% 100%)",
    modalInset: "hsl(220 30% 96%)",
    overlayBlur: "rgba(4, 19, 61, 0.35)",
  },
  dark: {
    foreground: "hsl(210 40% 98%)",
    mutedForeground: "hsl(215 20% 65%)",
    primary: "hsl(190 100% 50%)",
    primaryForeground: "hsl(230 40% 10%)",
    border: "hsl(230 30% 20%)",
    modalBg: "#0a1030",
    modalInset: "#071848",
    overlayBlur: "rgba(0, 0, 0, 0.6)",
  },
};

export function resolveOnboardThemeMode(
  resolvedTheme?: string | null,
): OnboardThemeMode {
  if (resolvedTheme === "light" || resolvedTheme === "dark") {
    return resolvedTheme;
  }

  if (typeof document === "undefined") {
    return "dark";
  }

  const html = document.documentElement;
  if (html.classList.contains("dark")) return "dark";
  if (html.classList.contains("light")) return "light";

  const stored =
    typeof localStorage !== "undefined"
      ? localStorage.getItem(THEME_STORAGE_KEY)
      : null;
  if (stored === "light" || stored === "dark") return stored;

  return "dark";
}

function withAlpha(hsl: string, alpha: number): string {
  if (hsl.startsWith("#")) {
    return hsl;
  }
  const inner = hsl.replace(/^hsl\(/, "").replace(/\)$/, "");
  return `hsl(${inner} / ${alpha})`;
}

/** Maps portal-bridge theme mode to the coti-wallet-plugin OnboardModal theme. */
export function buildOnboardModalTheme(mode?: OnboardThemeMode): OnboardModalTheme {
  const palette = ONBOARD_PALETTES[mode ?? "dark"];
  const {
    foreground,
    mutedForeground,
    primary,
    primaryForeground,
    border,
    modalBg,
    modalInset,
    overlayBlur,
  } = palette;

  return {
    backdrop: {
      backgroundColor: overlayBlur,
      backdropFilter: "blur(4px)",
      zIndex: 60,
    },
    modal: {
      backgroundColor: modalBg,
      border: `1px solid ${border}`,
      borderRadius: "16px",
      color: foreground,
      boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    },
    closeButton: {
      color: mutedForeground,
    },
    iconContainer: {
      backgroundColor: withAlpha(primary, 0.1),
      border: `1px solid ${withAlpha(primary, 0.2)}`,
    },
    title: {
      color: foreground,
    },
    description: {
      color: mutedForeground,
    },
    infoBox: {
      backgroundColor: withAlpha(foreground, 0.05),
      border: `1px solid ${border}`,
    },
    infoText: {
      color: foreground,
    },
    errorBox: {
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      border: "1px solid rgba(239, 68, 68, 0.3)",
    },
    errorText: {
      color: "#f87171",
    },
    primaryButton: {
      backgroundColor: primary,
      color: primaryForeground,
      borderRadius: "12px",
    },
    primaryButtonDisabled: {
      backgroundColor: withAlpha(primary, 0.5),
      color: withAlpha(primaryForeground, 0.6),
      borderRadius: "12px",
    },
    cancelButton: {
      color: mutedForeground,
    },
    spinner: {
      border: `2px solid ${withAlpha(foreground, 0.2)}`,
      borderTopColor: primary,
    },
    stepLabel: {
      color: foreground,
    },
    stepDescription: {
      color: mutedForeground,
    },
    stepIconPending: {
      backgroundColor: withAlpha(foreground, 0.08),
      border: `2px solid ${withAlpha(foreground, 0.15)}`,
    },
    stepIconActive: {
      backgroundColor: withAlpha(primary, 0.1),
      border: `2px solid ${primary}`,
    },
    stepIconComplete: {
      backgroundColor: "rgba(34, 197, 94, 0.1)",
      border: "2px solid #22c55e",
    },
    stepIconError: {
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      border: "2px solid #ef4444",
    },
    aesKeyBox: {
      backgroundColor: modalInset,
      border: `1px solid ${border}`,
      color: primary,
    },
    inlineIconButton: {
      backgroundColor: withAlpha(primary, 0.1),
      color: primary,
      border: `1px solid ${withAlpha(primary, 0.3)}`,
      borderRadius: "12px",
    },
    warningBox: {
      padding: "0",
    },
    warningText: {
      color: mutedForeground,
    },
    calloutBox: {
      backgroundColor: withAlpha(primary, 0.1),
      border: `1px solid ${withAlpha(primary, 0.3)}`,
    },
    calloutText: {
      color: primary,
    },
  };
}

/** Debug snapshot for console — compares DOM vs resolved mode. */
export function getOnboardModalThemeDebugInfo(
  theme?: OnboardModalTheme,
  mode?: OnboardThemeMode,
) {
  if (typeof document === "undefined") {
    return { environment: "ssr" as const };
  }

  const html = document.documentElement;
  const style = getComputedStyle(html);
  const activeMode = mode ?? resolveOnboardThemeMode();
  const built = theme ?? buildOnboardModalTheme(activeMode);
  const domDark = html.classList.contains("dark");
  const domLight = html.classList.contains("light");

  return {
    activeMode,
    domMatchesMode:
      (activeMode === "dark" && domDark) || (activeMode === "light" && domLight),
    htmlClass: html.className,
    htmlHasDark: domDark,
    htmlHasLight: domLight,
    localStorageTheme:
      typeof localStorage !== "undefined"
        ? localStorage.getItem(THEME_STORAGE_KEY)
        : null,
    cssVarRaw: {
      modalCompactBg: style.getPropertyValue("--modal-compact-bg").trim(),
      foreground: style.getPropertyValue("--foreground").trim(),
    },
    resolvedThemeColors: {
      modalColor: built.modal?.color,
      modalBackgroundColor: built.modal?.backgroundColor,
      primaryButtonBackgroundColor: built.primaryButton?.backgroundColor,
      backdropBackgroundColor: built.backdrop?.backgroundColor,
    },
  };
}

/**
 * Live theme for OnboardModal — driven by next-themes resolvedTheme, not getComputedStyle,
 * so the onboard screen stays correct even when the html class lags behind React state.
 */
export function useOnboardModalTheme(): OnboardModalTheme {
  const { resolvedTheme, theme, systemTheme, forcedTheme } = useTheme();
  const mode = useMemo(
    () => resolveOnboardThemeMode(resolvedTheme ?? theme),
    [resolvedTheme, theme],
  );

  const [onboardTheme, setOnboardTheme] = useState<OnboardModalTheme>(() =>
    buildOnboardModalTheme(mode),
  );

  useLayoutEffect(() => {
    const next = buildOnboardModalTheme(mode);
    const debug = getOnboardModalThemeDebugInfo(next, mode);
    if (!debug.domMatchesMode) {
      console.warn(
        "[useOnboardModalTheme] resolvedTheme mode does not match html class yet — using palette for mode",
        { mode, resolvedTheme, theme, systemTheme, forcedTheme, debug },
      );
    } else {
      console.log("[useOnboardModalTheme] Theme refreshed", {
        mode,
        resolvedTheme,
        theme,
        systemTheme,
        forcedTheme,
        debug,
      });
    }
    setOnboardTheme(next);
  }, [mode, resolvedTheme, theme, systemTheme, forcedTheme]);

  return onboardTheme;
}
